import { KeyRound, LogOut, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { SettingsPanel } from '@/components/layout/SettingsPanel'
import { useAuth } from '@/hooks/useAuth'
import { useAuth0 } from '@/hooks/useAuth0'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

/*
 * The trigger sits centred in a 6.5rem rail, so "to the right of the trigger"
 * is still on top of the rail. This is the gap that clears its edge with a
 * little air, expressed in rem and converted only because Radix's sideOffset
 * takes a pixel number.
 */
const RAIL_CLEARANCE_REM = 2.75
const ROOT_FONT_PX = 16

export default function UserMenu() {
  const navigate = useNavigate()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { user, logout, isClientAdmin } = useAuth()
  const { user: auth0User, logout: auth0Logout, isAuthenticated: isAuth0Authenticated } = useAuth0()

  // Use Auth0 user if authenticated via Auth0, otherwise fall back to legacy auth
  const currentUser = isAuth0Authenticated && auth0User ? {
    firstName: auth0User.firstName || auth0User.name?.split(' ')[0],
    lastName: auth0User.lastName || auth0User.name?.split(' ').slice(1).join(' '),
    email: auth0User.email,
    name: auth0User.name,
    role: auth0User.role,
  } : user ? {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
    role: undefined,
  } : null

  const handleLogout = isAuth0Authenticated ? auth0Logout : logout

  if (!currentUser) return null

  // Get user initials for avatar - matches Consumer Duty style
  const getUserInitials = () => {
    // Try name first
    const nameFromUser = currentUser.name?.trim()
    // Then try firstName + lastName
    const nameFromParts = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
    const nameToUse = nameFromUser || nameFromParts

    if (nameToUse && nameToUse.length > 0) {
      const nameParts = nameToUse.split(' ').filter(p => p.length > 0)
      const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || ''
      const secondInitial = nameParts.length > 1 ? nameParts[1]?.charAt(0).toUpperCase() : ''
      return firstInitial + secondInitial || firstInitial
    }

    // Fallback to email if no name
    if (currentUser.email) {
      return currentUser.email
        .split('@')[0]
        .substring(0, 2)
        .toUpperCase()
    }

    return '?'
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus-ring relative h-10 w-10 rounded-full p-0 group outline-none" aria-label="Open user menu">
          {/*
            Solid accent. On the deep purple rail the previous fill sat at
            1.10:1 against its own background -- readable initials inside a
            circle you could not see. This clears 3.09:1 for the circle and
            5.92:1 for the text.
          */}
          <div className="h-10 w-10 rounded-full bg-accent text-text-nav text-body font-medium flex items-center justify-center leading-none group-hover:bg-accent-hover transition-colors duration-fast">
            {/*
              Optically centred, not geometrically. Flex centres the text's
              advance box; what a reader sees is the ink inside it, and the two
              do not coincide for capitals:

                - "D" carries a left side-bearing and "U" almost none on the
                  right, so the ink sits ~0.02rem right of the box centre.
                - Capitals have no descenders, so the ink fills only the upper
                  part of the line box and reads ~0.03rem high.

              The nudge cancels both. Measured from the rendered glyphs rather
              than guessed.
            */}
            <span className="translate-x-[-0.02rem] translate-y-[0.03rem]">
              {getUserInitials()}
            </span>
          </div>
        </button>
      </DropdownMenuTrigger>

      {/*
        Anchored for a trigger at the foot of the left rail: out to the right,
        bottom-aligned with the avatar. `align="end"` alone was written for the
        old top-right header avatar, where it pulled the menu leftwards -- from
        down here that threw it across the rail instead.
      */}
      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={RAIL_CLEARANCE_REM * ROOT_FONT_PX}
        className="w-64"
      >
        {/*
          px-8 left 12rem of a 16rem menu for an avatar and an email address,
          which is not enough for either.
        */}
        <div className="m-1 rounded-md bg-surface-sunken px-3 py-3">
          <div className="flex flex-row items-center gap-x-3">
            <div>
              {/*
                The same accent as the trigger. It was bg-action here and
                bg-accent there, so one user wore two colours depending on
                whether their own menu was open.
              */}
              <div className="h-12 w-12 shrink-0 rounded-full bg-accent text-text-nav text-h4 font-medium flex items-center justify-center leading-none">
                {getUserInitials()}
              </div>
            </div>
            {/* min-w-0, or the email below cannot shrink and truncate. */}
            <div className="flex min-w-0 flex-col justify-center">
              <div className="truncate text-body-small font-medium text-text">
                {currentUser.name || 'User'}
              </div>
              <div className="truncate text-caption text-text-muted" title={currentUser.email}>
                {currentUser.email}
              </div>
              {currentUser.role && (
                <div className="mt-0.5 text-caption font-medium text-text-muted">
                  {currentUser.role}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Activate Admin - only for users who are not yet a Client Admin (OR-312) */}
        {!isClientAdmin && (
          <DropdownMenuItem
            onClick={() => navigate('/activate-admin')}
            className="cursor-pointer px-4 py-2 text-body-small text-text"
          >
            <KeyRound size={16} className="mr-2" />
            Activate Admin
          </DropdownMenuItem>
        )}

        {/*
          The chrome preset chooser. It lost its home when the rail's settings
          control became the light/dark toggle, which left the presets applied
          from localStorage with no way to change them.
        */}
        <DropdownMenuItem
          onClick={() => setSettingsOpen(true)}
          className="cursor-pointer px-4 py-2 text-body-small text-text"
        >
          <SlidersHorizontal size={16} className="mr-2" />
          Navigation Settings
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Logout */}
        <DropdownMenuItem
          onClick={() => handleLogout()}
          className="cursor-pointer px-4 py-2 text-body-small text-text"
        >
          <LogOut size={16} className="mr-2" />
          Log Out
        </DropdownMenuItem>
      </DropdownMenuContent>

      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </DropdownMenu>
  )
}
